import { ArrowUpRight, Facebook, Instagram, Youtube } from "lucide-react";
import { track } from "@/lib/customer-intelligence/tracker";

function XLogoIcon({ size = 18, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function WhatsAppIcon({ size = 18, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.52 3.48A11.85 11.85 0 0 0 12.05 0C5.5 0 .2 5.3.2 11.85c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.83 11.83 0 0 0 5.64 1.44h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.17-1.23-6.15-3.38-8.43ZM12.05 21.4h-.01a9.55 9.55 0 0 1-4.87-1.33l-.35-.21-3.8 1 1.01-3.7-.23-.38a9.53 9.53 0 0 1-1.46-5.03c0-5.27 4.29-9.56 9.57-9.56 2.55 0 4.95.99 6.76 2.8a9.5 9.5 0 0 1 2.8 6.76c0 5.28-4.3 9.57-9.57 9.57Zm5.24-7.16c-.29-.14-1.7-.84-1.97-.94-.26-.1-.46-.14-.65.15-.19.29-.74.94-.91 1.13-.17.19-.34.22-.63.07-.29-.14-1.22-.45-2.32-1.43-.86-.77-1.44-1.71-1.61-2-.17-.29-.02-.44.13-.58.13-.13.29-.34.43-.5.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.65-1.57-.9-2.15-.24-.57-.48-.49-.65-.5-.17-.01-.36-.01-.55-.01a1.06 1.06 0 0 0-.77.36c-.26.29-1 .98-1 2.4s1.03 2.78 1.17 2.97c.14.19 2.02 3.09 4.9 4.33.68.29 1.22.47 1.63.6.68.22 1.31.19 1.8.11.55-.08 1.7-.69 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.19-.55-.33Z" />
    </svg>
  );
}

type Social = {
  label: string;
  href: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  /** Tailwind classes applied on hover for the brand-correct color. */
  hover: string;
};

const SOCIALS: Social[] = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/truweofficial/",
    icon: Instagram,
    // Instagram brand gradient
    hover:
      "hover:text-white hover:border-transparent hover:[background-image:linear-gradient(45deg,#f09433_0%,#e6683c_25%,#dc2743_50%,#cc2366_75%,#bc1888_100%)] hover:shadow-[0_8px_24px_-8px_rgba(220,39,67,0.55)]",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61591463848208",
    icon: Facebook,
    // Facebook brand blue
    hover:
      "hover:text-white hover:border-transparent hover:bg-[#1877F2] hover:shadow-[0_8px_24px_-8px_rgba(24,119,242,0.55)]",
  },
  {
    label: "YouTube",
    href: "https://youtube.com/@truwefuturenutrition?si=4ddsaBZyjCjD8sKS",
    icon: Youtube,
    // YouTube brand red
    hover:
      "hover:text-white hover:border-transparent hover:bg-[#FF0000] hover:shadow-[0_8px_24px_-8px_rgba(255,0,0,0.55)]",
  },
  {
    label: "X.com",
    href: "https://x.com/TruweIndia",
    icon: XLogoIcon,
    // X brand black
    hover:
      "hover:text-white hover:border-transparent hover:bg-[#000000] hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.55)]",
  },
  {
    label: "WhatsApp",
    href: "https://whatsapp.com/channel/0029Vb7xJhFKQuJBtTLE7F2r",
    icon: WhatsAppIcon,
    // WhatsApp brand green
    hover:
      "hover:text-white hover:border-transparent hover:bg-[#25D366] hover:shadow-[0_8px_24px_-8px_rgba(37,211,102,0.55)]",
  },
];

function trackSocialClick(network: string, href: string, location: string = "footer") {
  const net = network.toLowerCase();
  try {
    // Persisted analytics (customer_events → admin panel)
    track("social_click", { network: net, location, href });
  } catch {
    // never break navigation
  }
  try {
    const payload = {
      event: "social_click",
      social_network: net,
      link_url: href,
      location,
      timestamp: new Date().toISOString(),
    };
    const w = window as unknown as {
      dataLayer?: Array<Record<string, unknown>>;
      gtag?: (...args: unknown[]) => void;
    };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(payload);
    w.gtag?.("event", "social_click", {
      social_network: net,
      link_url: href,
      location,
    });
    window.dispatchEvent(new CustomEvent("truwe:social_click", { detail: payload }));
  } catch {
    // analytics must never break navigation
  }
}

export { trackSocialClick };

export function SocialLinks({ className = "" }: { className?: string }) {
  return (
    <div className={`tw-social ${className}`}>
      <p className="tw-social__title">Follow TruWe</p>
      <ul className="tw-social__grid" aria-label="TruWe social profiles">
        {SOCIALS.map(({ label, href, icon: Icon }) => (
          <li key={label} className={label === "WhatsApp" ? "tw-social__channel" : undefined}>
            <a href={href} target="_blank" rel="noopener noreferrer"
              aria-label={`${label === "WhatsApp" ? "Join TruWe on" : "Follow TruWe on"} ${label} (opens in a new tab)`}
              data-analytics="social_click" data-social-network={label.toLowerCase()}
              onClick={() => trackSocialClick(label, href)}
              onAuxClick={() => trackSocialClick(label, href)}
              className="tw-social__link">
              <span className={`tw-social__icon tw-social__icon--${label.toLowerCase().replace(".com", "")}`}><Icon size={19} strokeWidth={1.8} /></span>
              <span className="tw-social__label">{label === "X.com" ? "X" : label}{label === "WhatsApp" && <small>Join our channel</small>}</span>
              <ArrowUpRight className="tw-social__arrow" size={14} aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
